import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../Modal';
import { useI18n } from '../../../i18n/I18nProvider';

const DEFAULT_OPTIONS = {
  title: '',
  message: '',
  confirmText: '',
  cancelText: '',
  isDestructive: false,
};

export function useConfirmationDialog() {
  const { t } = useI18n();
  const resolverRef = useRef<((result: boolean) => void) | null>(null);
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [open, setOpen] = useState(false);

  const closeDialog = useCallback((result: boolean) => {
    setOpen(false);
    setOptions(DEFAULT_OPTIONS);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  const confirm = useCallback(
    (nextOptions: {
      title?: string;
      message?: string;
      confirmText?: string;
      cancelText?: string;
      confirmColor?: string;
    }) =>
      new Promise<boolean>((resolve) => {
        resolverRef.current = resolve;
        setOptions({
          title: nextOptions?.title || '',
          message: nextOptions?.message || '',
          confirmText: nextOptions?.confirmText || t('common.confirm'),
          cancelText: nextOptions?.cancelText || t('common.cancel'),
          isDestructive: nextOptions?.confirmColor === 'error',
        });
        setOpen(true);
      }),
    [t],
  );

  const confirmationDialog = useMemo(
    () => (
      <Modal open={open} onClose={() => closeDialog(false)} size="sm">
        <ModalHeader title={options.title} onClose={() => closeDialog(false)} />
        <ModalBody>
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            {options.message}
          </p>
        </ModalBody>
        <ModalFooter
          onCancel={() => closeDialog(false)}
          cancelText={options.cancelText}
          onConfirm={() => closeDialog(true)}
          confirmText={options.confirmText}
          isDestructive={options.isDestructive}
        />
      </Modal>
    ),
    [closeDialog, open, options],
  );

  return { confirm, confirmationDialog };
}
